import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TableRow as MuiTableRow } from '@mui/material';
import { colors } from '@sweep-io/sweep-design';
import { Reorder } from '@sweep-io/sweep-design/dist/icons';
import TableRowCells from './TableRowCells';
import { DataTableBaseColumn, DataTableRow, DataTableVariant, SxRowFunction } from './TableTypes';
import { dataTableVariants } from './dataTableVariants';
import { ACTION_BUTTONS_KEY, linearGradient } from './utils';
import { ActionCell } from './ActionCell';
import { useRef, useEffect, forwardRef, useCallback } from 'react';

interface RowProps<TRow extends DataTableRow> {
  row: TRow;
  columns: DataTableBaseColumn[];
  sxRowFunction?: SxRowFunction;
  allowRowReorder?: boolean;
  isBeingDragged: boolean;
  isOverlay: boolean;
  onClick?: (
    e: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
    columnData: DataTableBaseColumn,
  ) => void;
  variant: DataTableVariant;
  overColumnId?: string | null;
  draggedColumnId?: string | null;
  isColumnReorderAllowed?: boolean;
  isColumnResizeAllowed?: boolean;
}

export const DraggableTableRow = forwardRef<HTMLTableRowElement, RowProps<any>>(
  function DraggableTableRow<TRow extends DataTableRow = any>(
    {
      isOverlay,
      row,
      allowRowReorder,
      sxRowFunction,
      variant,
      onClick,
      isBeingDragged,
      overColumnId,
      draggedColumnId,
      isColumnReorderAllowed,
      isColumnResizeAllowed,
      ...rowProps
    }: RowProps<TRow>,
    ref: React.Ref<HTMLTableRowElement>,
  ) {
    const rowRef = useRef<HTMLTableRowElement | null>(null);
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id: row.id,
      disabled: row.disabled,
    });

    useEffect(() => {
      if (rowRef.current) {
        const height = rowRef.current.getBoundingClientRect().height;
        // Set CSS variable for the row height
        document.documentElement.style.setProperty(`--row-height-${row.id}`, `${height}px`);
      }
    }, [row.id, isBeingDragged]);

    const _setNodeRef = useCallback(
      (node: HTMLTableRowElement | null) => {
        setNodeRef(node);
        if (node) {
          rowRef.current = node;
        }
        if (ref && typeof ref === 'function') ref(node);
        else if (ref && typeof ref === 'object') (ref as any).current = node;
      },
      [setNodeRef, ref],
    );

    const style = {
      transform: CSS.Translate.toString(transform),
      transition: transition || undefined,
      height: isBeingDragged ? `var(--row-height-${row.id})` : undefined,
    };

    const sxRow = sxRowFunction ? sxRowFunction(Boolean(isBeingDragged)) : {};

    const isRenderEmpty = isBeingDragged && !isOverlay;
    const actionButtons = row[ACTION_BUTTONS_KEY];

    return (
      <MuiTableRow
        ref={isOverlay ? setNodeRef : _setNodeRef}
        data-testid="data-table-row"
        data-dragging={isBeingDragged}
        style={style}
        {...attributes}
        sx={{
          minHeight: dataTableVariants[variant].minRowHeight,
          backgroundColor: row.isRowActive ? colors.grey[150] : 'transparent',
          position: isBeingDragged ? 'relative' : undefined,
          '&[data-dragging="true"]': {
            height: `var(--row-height-${row.id})`,
            opacity: isOverlay ? 1 : 0.5,
          },
          '&[data-dragging="false"]': {
            height: `var(--row-height-${row.id})`,
          },
          '&:hover, &[data-menu-open="true"]': {
            '.action-buttons': {
              '&:before': {
                background: linearGradient(colors.grey[150]),
                visibility: isBeingDragged ? 'hidden' : 'visible',
                width: '100%',
              },
              '.custom-action-button': {
                opacity: isBeingDragged ? '0 !important' : '1 !important',
              },
            },
          },
          '.action-buttons:before': {
            background: row.isRowActive ? linearGradient(colors.grey[150]) : undefined,
          },
          ...sxRow,
          animation: row._animation ? row._animation : '',
          cursor: row.isClickable ? 'pointer' : 'default',
        }}
        className="SweepDataTableRow"
      >
        <TableRowCells
          isRenderEmpty={isRenderEmpty}
          row={row}
          allowReorder={!!allowRowReorder}
          dragHandle={<Reorder {...listeners} color={colors.grey[800]} />}
          variant={variant}
          onClick={
            !!onClick
              ? (
                  e: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
                  column: DataTableBaseColumn,
                ) => onClick(e, column)
              : undefined
          }
          overColumnId={overColumnId}
          draggedColumnId={draggedColumnId}
          isColumnReorderAllowed={isColumnReorderAllowed}
          isColumnResizeAllowed={isColumnResizeAllowed}
          {...rowProps}
        />
        {(!!onClick || actionButtons) && (
          <ActionCell
            row={row}
            variant={variant}
            sxRowFunction={sxRowFunction}
            onClick={onClick}
            {...rowProps}
          />
        )}
      </MuiTableRow>
    );
  },
);
